import { memo, useCallback, useState } from "react";
import { VisuallyHidden } from "react-aria";
import { useLocation } from "react-router";
import { getWidgetColorClassName } from "@/components/menu/helpers/getWidgetColorClassName";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useUserData } from "@/components/user/lib/hooks/useUserData/useUserData";
import { useMosaic } from "@/context/mosaic/MosaicContext";
import { useTabs } from "@/context/tabs/TabsContext";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";
import { isEnabledChat } from "@/lib/utils/features";
import { AuthServiceGold } from "@/services/user-auth/auth-service-gold";
import { ChatIcon } from "@/shared/assets/icons/ui/ChatIcon";
import { PhoneNumberForm } from "@/shared/components/PhoneNumberForm";
import { ROUTES } from "@/shared/constants/router";
import { LocalStorageKeys, type ViewId } from "@/shared/types/common";

interface Props {
	className?: string;
	placing?: "header" | "menu";
}

export const ChatIconWithPhoneForm: React.FC<Props> = memo(
	({ className, placing = "header", ...otherProps }) => {
		const { authService } = useAuth();
		const { getTabLayout } = useTabs();
		const { userData } = useUserData();
		const { addMosaicWindow, removeMosaicWindow } = useMosaic();
		const [openModal, setOpenModal] = useState(false);
		const location = useLocation();

		const phoneConfirmed = !!sessionStorage.getItem(
			LocalStorageKeys.phoneConfirm,
		);

		const showChatIcon =
			isEnabledChat() &&
			authService?.allowedWidgets.includes("Chat") &&
			location.pathname === ROUTES.DASHBOARD;

		const onChatIconClick = useCallback(() => {
			if (
				!(authService instanceof AuthServiceGold) &&
				!userData?.phoneConfirmed &&
				phoneConfirmed !== true
			) {
				setOpenModal(true);
				return;
			}

			setOpenModal(false);

			const tabLayout = getTabLayout();
			const secondPart =
				typeof tabLayout === "object" ? tabLayout?.second : null;
			const isChatOpen =
				typeof secondPart === "string" && secondPart.startsWith("Chat");

			if (isChatOpen && typeof secondPart === "string") {
				removeMosaicWindow(secondPart as ViewId);
			} else if (!isChatOpen) {
				addMosaicWindow("Chat");
			}

			// Close the menu by simulating an Escape key press or clicking outside
			try {
				const burgerMenu = document.querySelector("[data-testid='menubar']");
				if (burgerMenu) {
					// Simulate escape key press
					const escapeEvent = new KeyboardEvent("keydown", {
						key: "Escape",
						code: "Escape",
						keyCode: 27,
						bubbles: true,
					});
					document.dispatchEvent(escapeEvent);
				}
			} catch (error) {
				console.warn("Failed to close menu:", error);
			}
		}, [
			authService,
			userData?.phoneConfirmed,
			userData,
			getTabLayout,
			removeMosaicWindow,
			addMosaicWindow,
			phoneConfirmed,
		]);

		if (!showChatIcon) return null;

		return (
			<div className={cn("", className)} {...otherProps}>
				{placing === "header" ? (
					<ChatIcon
						className="w-4 h-4 mr-1 cursor-pointer"
						stroke="white"
						fill="white"
						onClick={onChatIconClick}
						aria-label="Open Chat"
					/>
				) : (
					<button
						type="button"
						className={cn(
							"cursor-pointer px-1 ml-3 rounded transition-all duration-300",
							getWidgetColorClassName("Chat"),
						)}
						onClick={onChatIconClick}
					>
						Chat
					</button>
				)}

				<Dialog
					open={openModal}
					onOpenChange={setOpenModal}
					data-testid="modal"
				>
					<DialogContent className="gap-0 p-0">
						<VisuallyHidden>
							<DialogTitle>Confirm Phone Number</DialogTitle>
						</VisuallyHidden>

						<PhoneNumberForm
							className="md:w-full border-none p-0"
							showTitle={false}
							onClose={(phone) => {
								if (phone && userData) {
									userData.phone = phone; // optimistic update
								}
								setOpenModal(false);
							}}
							onSuccess={() => {
								if (userData) {
									userData.phoneConfirmed = true; // optimistic update
								}
								sessionStorage.setItem(LocalStorageKeys.phoneConfirm, "true");

								onChatIconClick(); // re-trigger chat icon click to open chat
							}}
						/>
					</DialogContent>
				</Dialog>
			</div>
		);
	},
);
ChatIconWithPhoneForm.displayName = "ChatIconWithPhoneForm";
